import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import {
  CourseSelect,
  GradeSelect,
  StudentPurchaseableSubjectsSelect,
} from "../../../../components";
import { ActivityButton } from "../../../../components/Common";
import {
  LocalizedProperty,
  ManualPurchaseRequest,
  PhoneDto,
  State,
} from "../../../../interfaces";
import {
  manualPurchaseSubject,
  resetManualPurchaseSubject,
} from "../../../../store/actions";
import i18n from "../../../../i18n";
import { PaymentThroughEnum } from "../../../../enums";

type TProps = {
  userId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  name: LocalizedProperty | null;
  phoneNumber: PhoneDto;
};
export const ManualPurchaseSubjectModal: React.FC<TProps> = ({
  userId,
  show,
  onCloseClick,
  name,
  phoneNumber,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    loading: manualPurchaseSubjectLoading,
    error: manualPurchaseSubjectError,
    success: manualPurchaseSubjectSuccess,
  } = useSelector(
    (state: any): State<any> => state.purchase.manualPurchaseSubject
  );
  const [selectedSubjects, setSelectedSubjects] = useState<any[]>();

  //#region form
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: "",
      gradeId: "",
      subjects: [],
      invoiceId: null,
      amount: 0,
      currency: "",
      paymentThrough: "",
    },
    initialErrors: manualPurchaseSubjectError,
    validationSchema: Yup.object({
      courseId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "course",
        })
      ),
      gradeId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "grade",
        })
      ),
      subjects: Yup.array(),
      invoiceId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "invoiceId",
        })
      ),
      paymentThrough: Yup.string().required(),
      amount: Yup.string().when("paymentThrough", {
        is: "OTHER",
        then: () =>
          Yup.number().required(
            t("isNotEmpty", {
              displayName: "amount",
            })
          ),
      }),
      currency: Yup.string().when("paymentThrough", {
        is: "OTHER",
        then: () =>
          Yup.string().required(
            t("isNotEmpty", {
              displayName: "currency",
            })
          ),
      }),
    }),
    onSubmit: (values) => {
      if (
        !selectedSubjects ||
        !selectedSubjects.length ||
        selectedSubjects?.length === 0
      ) {
        toast.error(t("chooseAtLeast1Subject"), {
          autoClose: 3000,
        });
        return;
      }
      const request: ManualPurchaseRequest = {
        invoiceId: values.invoiceId,
        productId: selectedSubjects.map((m) => m.value),
        userId: userId,
        paymentThrough: values.paymentThrough as PaymentThroughEnum,
        amount: Number(values.amount || 0),
        currency: values.currency,
      };
      dispatch(manualPurchaseSubject(request, history));
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (!manualPurchaseSubjectLoading && manualPurchaseSubjectSuccess) {
      toast.success(t("manualPurchaseSuccess"), {
        autoClose: 3000,
      });
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [manualPurchaseSubjectLoading, manualPurchaseSubjectSuccess]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
    setSelectedSubjects([]);
    dispatch(resetManualPurchaseSubject());
  };

  const onChangeSubject = (changedValue: any) => {
    setSelectedSubjects(changedValue);
  };

  //#endregion

  return (
    <Modal
      id="manualPurchase"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <ToastContainer />
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {t("manualPurchase")}
        </ModalHeader>
        {typeof manualPurchaseSubjectError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{manualPurchaseSubjectError}</p>
          </Alert>
        )}
        <ModalBody>
          <div className="mb-3">
            <p>
              <strong>{t("name")}</strong>
              {": "}
              {name && name.en && name.ar
                ? i18n.language === "ar"
                  ? name.ar
                  : name.en
                : "n/a"}
            </p>
            <strong>
              {t("phoneNumber")} {": "}
            </strong>
            <a
              style={{ cursor: "pointer" }}
              href={`tel:+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}
            >
              <u>{`+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}</u>
            </a>
          </div>
          <div className="mb-3">
            <Label htmlFor="courseId" className="form-label">
              {t("course")}
              <span className="text-danger">*</span>
            </Label>
            <CourseSelect
              type="select"
              id="courseId"
              name="courseId"
              className="form-select"
              aria-label={t("selectCourse")}
              placeholder={t("selectCourse")}
              value={form.values.courseId}
              onChange={(e) => {
                form.handleChange(e);
                setSelectedSubjects([]);
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.courseId && form.errors.courseId ? true : false
              }
            ></CourseSelect>
            {form.touched.courseId && form.errors.courseId ? (
              <FormFeedback type="invalid">{form.errors.courseId}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="gradeId" className="form-label">
              {t("grade")}
              <span className="text-danger">*</span>
            </Label>
            <GradeSelect
              courseId={form.values.courseId}
              type="select"
              id="gradeId"
              name="gradeId"
              className="form-select"
              aria-label={t("selectGrade")}
              placeholder={t("selectGrade")}
              value={form.values.gradeId}
              onChange={(e) => {
                form.handleChange(e);
                setSelectedSubjects([]);
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.gradeId && form.errors.gradeId ? true : false
              }
            ></GradeSelect>
            {form.touched.gradeId && form.errors.gradeId ? (
              <FormFeedback type="invalid">{form.errors.gradeId}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="subjectId" className="form-label">
              {t("subjects")}
              <span className="text-danger">*</span>
              <span className="text-info">({t("chooseAtLeast1Subject")})</span>
            </Label>
            <StudentPurchaseableSubjectsSelect
              placeholder="filterBySubject"
              isMulti={true}
              closeMenuOnSelect={false}
              value={selectedSubjects}
              onChange={onChangeSubject}
              courseId={form.values.courseId}
              gradeId={form.values.gradeId}
              userId={userId}
            ></StudentPurchaseableSubjectsSelect>
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("paymentThrough")}
              <span className="text-danger">*</span>
            </label>
            <div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio1"
                  value={PaymentThroughEnum.MYFATOORAH}
                  checked={
                    form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                  }
                  onChange={() => {
                    form.setFieldValue("invoiceId", "");
                    form.setFieldValue("amount", "");
                    form.setFieldValue("currency", "");
                    form.setFieldValue(
                      "paymentThrough",
                      PaymentThroughEnum.MYFATOORAH
                    );
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio1">
                  {t(PaymentThroughEnum.MYFATOORAH)}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio2"
                  value={PaymentThroughEnum.OTHER}
                  checked={
                    form.values.paymentThrough === PaymentThroughEnum.OTHER
                  }
                  onChange={() => {
                    form.setFieldValue("invoiceId", "");
                    form.setFieldValue("amount", "");
                    form.setFieldValue("currency", "");
                    form.setFieldValue(
                      "paymentThrough",
                      PaymentThroughEnum.OTHER
                    );
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio2">
                  {t(PaymentThroughEnum.OTHER)}
                </label>
              </div>
            </div>
            {form.touched.paymentThrough && form.errors.paymentThrough ? (
              <FormFeedback type="invalid ">
                {form.errors.paymentThrough as string}
              </FormFeedback>
            ) : null}
          </div>
          {[
            PaymentThroughEnum.OTHER.toString(),
            PaymentThroughEnum.MYFATOORAH.toString(),
          ].includes(form.values.paymentThrough) && (
            <>
              {" "}
              <div className="mb-3">
                <label htmlFor="invoiceId" className="form-label">
                  {form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                    ? t("invoiceId")
                    : t("transactionReference")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  autoComplete="off"
                  auto
                  id="invoiceId"
                  name="invoiceId"
                  value={form.values.invoiceId || ""}
                  type="text"
                  className="form-control"
                  maxLength={25}
                  placeholder={
                    form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                      ? t("enterInvoiceId")
                      : t("enterTransactionReference")
                  }
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    if (form.values.paymentThrough === "MYFATOORAH") {
                      const regex =
                        /^(0*[0-9][0-9]*(\[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                      if (regex.test(value.toString())) {
                        form.setFieldValue("invoiceId", value);
                      } else {
                        form.setFieldValue("invoiceId", "");
                      }
                    } else {
                      form.setFieldValue("invoiceId", value);
                    }
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.invoiceId && form.errors.invoiceId
                      ? true
                      : false
                  }
                />
                {form.touched.invoiceId && form.errors.invoiceId ? (
                  <FormFeedback type="invalid">
                    {form.errors.invoiceId as string}
                  </FormFeedback>
                ) : null}
              </div>
              {form.values.paymentThrough === "OTHER" && (
                <>
                  {" "}
                  <div className="mb-3">
                    <label htmlFor="amount" className="form-label">
                      {t("price")}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      onChange={(e) => {
                        e.preventDefault();
                        const { value } = e.target;
                        const regex =
                          /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                        if (regex.test(value.toString())) {
                          form.setFieldValue("amount", value);
                        } else {
                          form.setFieldValue("amount", "");
                        }
                      }}
                      autoComplete="off"
                      id="amount"
                      name="amount"
                      value={form.values.amount}
                      type="text"
                      className="form-control"
                      placeholder={t("price")}
                      onBlur={form.handleBlur}
                      invalid={
                        form.touched.amount && form.errors.amount ? true : false
                      }
                    />
                    {form.touched.amount && form.errors.amount ? (
                      <FormFeedback type="invalid">
                        {form.errors.amount as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="currency" className="form-label">
                      {t("currency")}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      invalid={
                        form.touched.currency && form.errors.currency
                          ? true
                          : false
                      }
                      type="select"
                      id="currency"
                      name="currency"
                      value={form.values.currency}
                      className="form-select form-select-md"
                      onBlur={form.handleBlur}
                      onChange={form.handleChange}
                    >
                      <option value="">{t("currency")}</option>
                      <option value="KD">KD</option>
                      <option value="USD">USD</option>
                    </Input>
                    {form.touched.currency && form.errors.currency ? (
                      <FormFeedback type="invalid">
                        {form.errors.currency as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t("purchase")}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={manualPurchaseSubjectLoading}
            disabled={manualPurchaseSubjectLoading}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
